import ListWrapper from 'components/ListWrapper';
import Typography from 'components/Typography';
import ReactSwitch from 'react-switch';
import useNotifications from './useNotifications';
import styles from './Notifications.module.scss';
import {NotificationType} from 'types/notification-settings';

interface NotificationCardProps {
  title: string;
  description: string;
  notificationType: NotificationType;
  getNotification: any;
  getTemplate: any;
  handleToggleChange: any;
  loadingStates: Record<number, boolean>;
}

function NotificationCard({
  title,
  description,
  notificationType,
  getNotification,
  getTemplate,
  handleToggleChange,
  loadingStates,
}: NotificationCardProps) {
  const emailNotification = getNotification(notificationType, 'email');
  const smsNotification = getNotification(notificationType, 'sms');
  const template = getTemplate(notificationType);

  return (
    <div className={styles.notificationCard}>
      <div className={styles.cardHeader}>
        <Typography variant="h5" className={styles.cardTitle}>
          {title}
        </Typography>
        <Typography className={styles.cardDescription}>
          {description}
        </Typography>
      </div>

      <div className={styles.cardContent}>
        <div className={styles.channelSection}>
          <div className={styles.switchGroup}>
            <div className={styles.switchItem}>
              <label className={styles.switchLabel}>
                <span>via Email</span>
                <ReactSwitch
                  checked={emailNotification?.is_enabled}
                  onChange={(checked) =>
                    emailNotification &&
                    handleToggleChange(emailNotification, checked)
                  }
                  onColor="#4285f4"
                  handleDiameter={16}
                  height={20}
                  width={40}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  disabled={
                    !emailNotification || loadingStates[emailNotification.id]
                  }
                />
              </label>
            </div>
            <div className={styles.switchItem}>
              <label className={styles.switchLabel}>
                <span>via SMS</span>
                <ReactSwitch
                  checked={smsNotification?.is_enabled}
                  onChange={(checked) =>
                    smsNotification &&
                    handleToggleChange(smsNotification, checked)
                  }
                  onColor="#4285f4"
                  handleDiameter={16}
                  height={20}
                  width={40}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  disabled={
                    !smsNotification || loadingStates[smsNotification.id]
                  }
                />
              </label>
            </div>
          </div>
        </div>

        <div className={styles.templateSection}>
          <div className={styles.templateQuote}>
            {template || 'No template available'}
          </div>
        </div>
      </div>
    </div>
  );
}

function Notifications() {
  const {
    isLoading,
    loadingStates,
    handleToggleChange,
    getNotification,
    getTemplate,
  } = useNotifications();

  if (isLoading) {
    return null;
  }

  return (
    <ListWrapper className={styles.wrapper}>
      <div className={styles.content}>
        <NotificationCard
          title="New Customer Payment via Payment Links"
          description="Receive notifications when customers complete payments through payment links"
          notificationType="customer_payment"
          getNotification={getNotification}
          getTemplate={getTemplate}
          handleToggleChange={handleToggleChange}
          loadingStates={loadingStates}
        />

        <NotificationCard
          title="New Customer Comes"
          description="Get notified when a new customer registers in your system"
          notificationType="new_customer"
          getNotification={getNotification}
          getTemplate={getTemplate}
          handleToggleChange={handleToggleChange}
          loadingStates={loadingStates}
        />

        <NotificationCard
          title="New Order Comes"
          description="Receive alerts when customers place new orders"
          notificationType="new_order"
          getNotification={getNotification}
          getTemplate={getTemplate}
          handleToggleChange={handleToggleChange}
          loadingStates={loadingStates}
        />

        <NotificationCard
          title="New Route Dispatches"
          description="Get notified when delivery routes are dispatched"
          notificationType="new_route"
          getNotification={getNotification}
          getTemplate={getTemplate}
          handleToggleChange={handleToggleChange}
          loadingStates={loadingStates}
        />
      </div>
    </ListWrapper>
  );
}

export default Notifications;
